/**
 * Shell — the frame around every signed-in page of the customer app. Design:
 * designs/app/Shell.dc.html (desktop 1440, mobile 390), hand-off README "Navigation".
 *
 * Desktop: a 232px sidebar (logo, workspace switcher, ⌘K search, grouped nav, settings, help,
 * user) that collapses to a 64px icon rail, remembered per device; nav groups open and close,
 * also remembered. A 56px top bar: crumbs and title, the environment switcher, then the app's
 * top-bar items, then Ask Inferrex (⌘J).
 *
 * Mobile: a 52px app bar (menu or back, title, environment · workspace, bell, Ask) and 5 bottom
 * tabs; "More" and the menu button open the full navigation as a sheet. Detail pages pass
 * `back` and `showTabs={false}`.
 *
 * Presentation only. The app owns the data: it passes the navigation, the switchers and the
 * top-bar items in, so this component holds no API knowledge.
 */
import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown, ChevronLeft, Menu, PanelLeftClose, PanelLeftOpen, Search, Sparkles, type LucideIcon,
} from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../primitives/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../primitives/tooltip';
import { useMediaQuery } from '../hooks/use-media-query';
import { formatNumber } from '../utils';
import { useDeviceFlag, useDeviceSet } from './device-setting';

export interface ShellNavItem {
  readonly key: string;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly to: string;
  /** A plain count (systems connected, pipelines). */
  readonly count?: number | null | undefined;
  /** Things waiting for the person — drawn as the amber attention badge. */
  readonly attention?: number | null | undefined;
}

export interface ShellNavGroup {
  readonly key: string;
  /** Group heading. Leave out for the ungrouped top entries (Home, Review queue). */
  readonly label?: string | undefined;
  readonly items: readonly ShellNavItem[];
}

export interface ShellTab {
  readonly key: string;
  readonly label: string;
  readonly icon: LucideIcon;
  /** Where the tab goes. Leave out for "More", which opens the full navigation. */
  readonly to?: string | undefined;
  readonly attention?: number | null | undefined;
}

export interface ShellProps {
  readonly nav: readonly ShellNavGroup[];
  /** Below the groups: Workspace settings, Help and support. */
  readonly footerNav: readonly ShellNavItem[];
  /** Key of the nav item (and tab) for the current page. */
  readonly activeKey: string;
  readonly title: string;
  readonly crumbs?: readonly string[] | undefined;
  /** Full lockup for the open sidebar, and the mark for the rail. */
  readonly logo: ReactNode;
  readonly logoMark: ReactNode;
  /** The workspace switcher; told whether the sidebar is a rail. */
  readonly workspace: (opts: { collapsed: boolean }) => ReactNode;
  /** The user block at the foot of the sidebar. */
  readonly user: (opts: { collapsed: boolean }) => ReactNode;
  /** Opens search (⌘K). */
  readonly onSearch?: (() => void) | undefined;
  /** Above the sidebar footer, e.g. "Finish setup · 5 of 7". */
  readonly sidebarExtra?: ReactNode;
  readonly environment?: ReactNode;
  /** Capacity meter, live indicator, jobs, cloud shell, support, notifications — in that order. */
  readonly topBarItems?: ReactNode;
  readonly askOpen?: boolean | undefined;
  /** Opens or closes Ask Inferrex; also bound to ⌘J / Ctrl+J. */
  readonly onAsk?: (() => void) | undefined;
  /** Above the page content, e.g. the rematch banner. */
  readonly banner?: ReactNode;
  /** Force the phone layout; by default it follows the viewport (below 768px). */
  readonly mobile?: boolean | undefined;
  /** App-bar second line on mobile, e.g. the environment dot and "Production · Tidepool Labs". */
  readonly mobileSubtitle?: ReactNode;
  /** App-bar actions on mobile before Ask (the notifications bell). */
  readonly mobileActions?: ReactNode;
  readonly tabs: readonly ShellTab[];
  /** Which tab is current; defaults to `activeKey`. */
  readonly activeTab?: string | undefined;
  /** Hide the bottom tabs (detail pages pin their primary action instead). */
  readonly showTabs?: boolean | undefined;
  /** Mobile detail pages: the app bar leads with "back" instead of the menu. */
  readonly back?: { readonly label: string; readonly onBack: () => void } | undefined;
  readonly children: ReactNode;
}

export const SHELL_COLLAPSED_KEY = 'inferrex.shell.sidebarCollapsed';
export const SHELL_CLOSED_GROUPS_KEY = 'inferrex.shell.closedGroups';
export const SHELL_MOBILE_QUERY = '(max-width: 767px)';

function Count({ item }: { item: ShellNavItem }) {
  if (item.attention) {
    return <span className="ifx-nav__count ifx-nav__count--attention" aria-label={`${item.attention} need you`}>{formatNumber(item.attention)}</span>;
  }
  if (item.count !== undefined && item.count !== null) {
    return <span className="ifx-nav__count">{formatNumber(item.count)}</span>;
  }
  return null;
}

function NavLinkItem({ item, active, collapsed, onNavigate }: {
  item: ShellNavItem; active: boolean; collapsed: boolean; onNavigate?: (() => void) | undefined;
}) {
  const Icon = item.icon;
  const link = (
    <Link
      to={item.to}
      className="ifx-nav__item"
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? item.label : undefined}
      onClick={onNavigate}
    >
      <Icon className="ifx-nav__icon" aria-hidden="true" />
      {!collapsed && <span className="ifx-nav__label">{item.label}</span>}
      {!collapsed && <Count item={item} />}
      {collapsed && item.attention ? <span className="ifx-nav__dot" aria-hidden="true" /> : null}
    </Link>
  );
  if (!collapsed) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}{item.attention ? ` · ${item.attention}` : ''}</TooltipContent>
    </Tooltip>
  );
}

function NavGroups({ nav, activeKey, collapsed, closedGroups, toggleGroup, onNavigate }: {
  nav: readonly ShellNavGroup[];
  activeKey: string;
  collapsed: boolean;
  closedGroups: ReadonlySet<string>;
  toggleGroup?: ((key: string, closed: boolean) => void) | undefined;
  onNavigate?: (() => void) | undefined;
}) {
  const baseId = useId();
  return (
    <>
      {nav.map((group) => {
        const listId = `${baseId}-${group.key}`;
        // A closed group still shows its active entry, so the current page is never hidden.
        const closed = !collapsed && !!group.label && closedGroups.has(group.key);
        const items = closed ? group.items.filter((i) => i.key === activeKey) : group.items;
        return (
          <div className="ifx-nav__group" key={group.key} data-group={group.key}>
            {group.label && !collapsed && (
              <button
                type="button"
                className="ifx-nav__heading"
                aria-expanded={!closed}
                aria-controls={listId}
                onClick={() => toggleGroup?.(group.key, !closed)}
              >
                <span>{group.label}</span>
                <ChevronDown className="ifx-nav__chevron" aria-hidden="true" />
              </button>
            )}
            {group.label && collapsed && <span className="ifx-nav__rule" aria-hidden="true" />}
            <ul className="ifx-nav__list" id={listId} aria-label={group.label}>
              {items.map((item) => (
                <li key={item.key}>
                  <NavLinkItem item={item} active={item.key === activeKey} collapsed={collapsed} onNavigate={onNavigate} />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </>
  );
}

/** ⌘J / Ctrl+J toggles Ask Inferrex from anywhere. */
function useAskShortcut(onAsk: (() => void) | undefined) {
  useEffect(() => {
    if (!onAsk) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        onAsk();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onAsk]);
}

export function Shell(props: ShellProps) {
  const viewportMobile = useMediaQuery(SHELL_MOBILE_QUERY);
  const mobile = props.mobile ?? viewportMobile;
  const [collapsed, setCollapsed] = useDeviceFlag(SHELL_COLLAPSED_KEY, false);
  const [closedGroups, setGroupClosed] = useDeviceSet(SHELL_CLOSED_GROUPS_KEY);
  const [menuOpen, setMenuOpen] = useState(false);
  useAskShortcut(props.onAsk);

  const crumbs = props.crumbs?.length ? props.crumbs : undefined;
  const skip = <a className="ifx-skip" href="#ifx-main">Skip to content</a>;

  if (mobile) {
    const activeTab = props.activeTab ?? props.activeKey;
    const showTabs = props.showTabs !== false;
    return (
      <TooltipProvider delayDuration={300}>
        <div className="ifx-shell ifx-shell--mobile">
          {skip}
          <header className="ifx-appbar">
            {props.back ? (
              <button type="button" className="ifx-appbar__button" onClick={props.back.onBack} aria-label={props.back.label}>
                <ChevronLeft aria-hidden="true" />
              </button>
            ) : (
              <button type="button" className="ifx-appbar__button" onClick={() => setMenuOpen(true)} aria-label="Open navigation" aria-haspopup="dialog" aria-expanded={menuOpen}>
                <Menu aria-hidden="true" />
              </button>
            )}
            <div className="ifx-appbar__titles">
              <h1 className="ifx-appbar__title">{props.title}</h1>
              {props.mobileSubtitle && <span className="ifx-appbar__subtitle">{props.mobileSubtitle}</span>}
            </div>
            {props.mobileActions}
            {props.onAsk && (
              <button type="button" className="ifx-appbar__button ifx-appbar__ask" onClick={props.onAsk} aria-label="Ask Inferrex" aria-pressed={props.askOpen ?? false}>
                <Sparkles aria-hidden="true" />
              </button>
            )}
          </header>
          {props.banner}
          <main className="ifx-shell__main" id="ifx-main" tabIndex={-1}>{props.children}</main>
          {showTabs && (
            <nav className="ifx-tabbar" aria-label="Main">
              {props.tabs.map((tab) => {
                const Icon = tab.icon;
                const active = tab.key === activeTab;
                const inner = (
                  <>
                    <Icon className="ifx-tabbar__icon" aria-hidden="true" />
                    <span>{tab.label}</span>
                    {!active && tab.attention ? <span className="ifx-tabbar__count" aria-label={`${tab.attention} need you`}>{formatNumber(tab.attention)}</span> : null}
                  </>
                );
                return tab.to ? (
                  <Link key={tab.key} to={tab.to} className="ifx-tabbar__tab" aria-current={active ? 'page' : undefined}>{inner}</Link>
                ) : (
                  <button key={tab.key} type="button" className="ifx-tabbar__tab" aria-haspopup="dialog" aria-expanded={menuOpen} onClick={() => setMenuOpen(true)}>{inner}</button>
                );
              })}
            </nav>
          )}
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetContent side="left" className="ifx-menu-sheet">
              <SheetTitle className="ifx-sr-only">Navigation</SheetTitle>
              <SheetDescription className="ifx-sr-only">Every part of the app.</SheetDescription>
              {/* The app bar names the environment; switching it lives here on a phone. */}
              <div className="ifx-sidebar__top">{props.workspace({ collapsed: false })}{props.environment}</div>
              <nav className="ifx-nav" aria-label="Main">
                <NavGroups nav={props.nav} activeKey={props.activeKey} collapsed={false} closedGroups={new Set()} onNavigate={() => setMenuOpen(false)} />
              </nav>
              <div className="ifx-sidebar__footer">
                <ul className="ifx-nav__list">
                  {props.footerNav.map((item) => (
                    <li key={item.key}><NavLinkItem item={item} active={item.key === props.activeKey} collapsed={false} onNavigate={() => setMenuOpen(false)} /></li>
                  ))}
                </ul>
                {props.user({ collapsed: false })}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className="ifx-shell" data-collapsed={collapsed || undefined}>
        {skip}
        <aside className="ifx-sidebar" aria-label="Sidebar">
          <div className="ifx-sidebar__brand">
            {collapsed ? props.logoMark : props.logo}
            <button
              type="button"
              className="ifx-sidebar__collapse"
              onClick={() => setCollapsed(!collapsed)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
            >
              {collapsed ? <PanelLeftOpen aria-hidden="true" /> : <PanelLeftClose aria-hidden="true" />}
            </button>
          </div>
          <div className="ifx-sidebar__top">
            {props.workspace({ collapsed })}
            {props.onSearch && (
              <button type="button" className="ifx-search" onClick={props.onSearch} aria-label="Search or jump to (⌘K)" aria-keyshortcuts="Meta+K Control+K">
                <Search aria-hidden="true" />
                {!collapsed && <span className="ifx-search__text">Search or jump to…</span>}
                {!collapsed && <kbd className="ifx-kbd">⌘K</kbd>}
              </button>
            )}
          </div>
          <nav className="ifx-nav" aria-label="Main">
            <NavGroups nav={props.nav} activeKey={props.activeKey} collapsed={collapsed} closedGroups={closedGroups} toggleGroup={setGroupClosed} />
          </nav>
          {props.sidebarExtra && !collapsed && <div className="ifx-sidebar__extra">{props.sidebarExtra}</div>}
          <div className="ifx-sidebar__footer">
            <ul className="ifx-nav__list" aria-label="Workspace">
              {props.footerNav.map((item) => (
                <li key={item.key}><NavLinkItem item={item} active={item.key === props.activeKey} collapsed={collapsed} /></li>
              ))}
            </ul>
            {props.user({ collapsed })}
          </div>
        </aside>
        <div className="ifx-shell__body">
          <header className="ifx-topbar">
            <div className="ifx-topbar__titles">
              {crumbs && <span className="ifx-topbar__crumbs">{crumbs.join(' / ')} /</span>}
              <h1 className="ifx-topbar__title">{props.title}</h1>
            </div>
            {props.environment}
            <div className="ifx-topbar__spacer" />
            {props.topBarItems}
            {props.onAsk && (
              <button
                type="button"
                className="ifx-ask"
                aria-pressed={props.askOpen ?? false}
                aria-keyshortcuts="Meta+J Control+J"
                onClick={props.onAsk}
              >
                <Sparkles aria-hidden="true" />Ask Inferrex
                {!props.askOpen && <span className="ifx-ask__kbd" aria-hidden="true">⌘J</span>}
              </button>
            )}
          </header>
          {props.banner}
          <main className="ifx-shell__main" id="ifx-main" tabIndex={-1}>{props.children}</main>
        </div>
      </div>
    </TooltipProvider>
  );
}

